import { semanticColors } from '@styles/design-system';
import React from 'react';

export interface SegmentOption {
    label: string;
    value: string;
}

interface SegmentedToggleProps {
    value: string;
    onChange: (value: string) => void;
    options: SegmentOption[];
    className?: string;
    /** Matches the control it sits beside. 30 is Mantine's xs input; 36 its default. */
    height?: number;
}

const SegmentedToggle: React.FC<SegmentedToggleProps> = ({
    value,
    onChange,
    options,
    className,
    height = 30,
}) => {
    return (
        <div
            className={className}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0,
            }}
        >
            {options.map((opt, idx) => {
                const active = opt.value === value;
                const isFirst = idx === 0;
                const isLast = idx === options.length - 1;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => onChange(opt.value)}
                        style={{
                            padding: '0 12px',
                            height,
                            fontSize: 12,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            border: '1px solid',
                            /*
                             * The selected segment has to be obvious at a glance.
                             *
                             * It used to differ by a 5% border opacity and a darker label, on a
                             * white background where its white fill showed as nothing — so on a
                             * pale panel the two read as the same control twice. It now carries
                             * the app's info blue on all three channels: border, label and a
                             * tinted fill.
                             *
                             * Tinted rather than filled solid: this picks a view, and a solid
                             * accent button beside a plain one reads as something to press.
                             */
                            borderColor: active
                                ? semanticColors.info.primary
                                : 'rgba(0,0,0,0.1)',
                            borderRadius: isFirst
                                ? '6px 0 0 6px'
                                : isLast
                                  ? '0 6px 6px 0'
                                  : 0,
                            marginLeft: isFirst ? 0 : -1,
                            fontWeight: active ? 600 : 500,
                            color: active
                                ? semanticColors.info.dark
                                : 'rgb(100, 116, 139)',
                            backgroundColor: active
                                ? semanticColors.info.light
                                : 'transparent',
                            // Above its neighbour, so the shared 1px edge is the accent colour
                            // rather than whichever button happens to paint last.
                            position: 'relative',
                            zIndex: active ? 1 : 0,
                            transition: 'color 0.15s, background-color 0.15s, border-color 0.15s',
                        }}
                        onMouseEnter={(e) => {
                            if (!active) {
                                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.03)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!active) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }
                        }}
                    >
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
};

export default SegmentedToggle;
